/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	Container,
	Flex,
	FormControl,
	FormHelperText,
	List,
	ListIcon,
	ListItem,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import FileFormField from '../../components/form/FileFormField';
import InputNumberFormField from '../../components/form/InputNumberFormField';
import RadioFormField from '../../components/form/RadioFormField';
import TextAreaFormField from '../../components/form/TextAreaFormField';
import LoadingOverlayText from '../../components/LoadingOverlay/LoadingOverlayText';
import c from '../../constant';
import DateTimeFormField from '../../utils/DateTimeFormField';
import { getWorkingDays } from '../../utils/leave-utils';

const validationSchema = Yup.object().shape({
	type: Yup.string().required('required'),
	leaveAmount: Yup.mixed()
		.test('not-empty', 'required', function (value) {
			return !value ? false : true;
		})
		.test('valid', 'invalid', function (value) {
			return value % 0.5 === 0;
		})
		.test('valid-count', 'incorrect leave amount input', function (value) {
			const { leaveFromDate, leaveToDate } = this.parent;
			if (leaveFromDate && leaveToDate) {
				const { count } = getWorkingDays(leaveFromDate, leaveToDate);
				return count === value;
			}
		}),
	leaveFromDate: Yup.date().required('required'),
	leaveToDate: Yup.date()
		.required('required')
		.test('is-after-from-date', 'not allowed before or same as from date', function (value) {
			const { leaveFromDate } = this.parent;
			if (!value) return false;
			return moment(value).isAfter(moment(leaveFromDate));
		}),
	leaveComebackDate: Yup.date()
		.required('required')
		.test('is-after-to-date', 'not allowed before to date', function (value) {
			const { leaveToDate } = this.parent;
			if (!value) return false;
			return moment(value).isSameOrAfter(moment(leaveToDate));
		})
		.test('is-skip-working', 'incorrect comback date input', function (value) {
			const { leaveToDate } = this.parent;
			if (leaveToDate) {
				const { count } = getWorkingDays(leaveToDate, value);
				return count === 0;
			}
		}),
	purpose: Yup.string().when('type', {
		is: 'ANNUAL_LEAVE',
		then: (schema) => schema.required('required'),
		otherwise: (schema) => schema.notRequired(),
	}),
	attachmentFile: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 20;
		} else {
			return true;
		}
	}),
	createRestriction: Yup.mixed()
		.test('valid', 'Leave request cannot be submitted now', function (value) {
			const { leaveFromDate } = this.parent;
			const hours = moment(leaveFromDate).get('hours');
			if (hours === 8) {
				const offset = moment(leaveFromDate).set('hours', hours).set('minutes', 30).set('seconds', 0);
				const now = moment();
				return now.isBefore(offset);
			}
			return true;
		})
		.test('valid', 'Leave request cannot be submitted now', function (value) {
			const { leaveFromDate } = this.parent;
			const hours = moment(leaveFromDate).get('hours');
			if (hours === 12) {
				const offset = moment(leaveFromDate).set('hours', hours).set('minutes', 0).set('seconds', 0);
				const now = moment();
				return now.isBefore(offset);
			}
			return true;
		}),
});

const CREATE_MUTATION = gql`
	mutation create(
		$type: String!
		$purpose: String!
		$from: DateTime!
		$to: DateTime!
		$comeback: DateTime!
		$amount: Float!
		$attachmentFile: Upload
		$attachmentFileSize: Float
	) {
		createLeave(
			leaveInput: { type: $type, purpose: $purpose, from: $from, to: $to, comeback: $comeback, amount: $amount }
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
		)
	}
`;

const QUERY_LEAVE_LEFT = gql`
	query {
		getLeaveAmountLeft {
			annualLeave
			shortLeave
		}
	}
`;

function AddLeaveRequest() {
	moment.locale('en-gb');

	const cancelRef = React.useRef();
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [createMutation, { loading }] = useMutation(CREATE_MUTATION);
	const [queryLeaveLeft, { data: dataLeaveLeft }] = useLazyQuery(QUERY_LEAVE_LEFT);

	useEffect(() => window.scrollTo(0, 0), []);

	useEffect(queryLeaveLeft, []);

	async function submit(values) {
		const yearFrom = moment(values?.leaveFromDate).year();
		const yearTo = moment(values?.leaveToDate).year();
		if (yearFrom !== yearTo) {
			onOpen();
		} else {
			const variables = {
				type: values?.type,
				purpose: values?.purpose || '-',
				from: moment(values?.leaveFromDate).toDate(),
				to: moment(values?.leaveToDate).toDate(),
				comeback: moment(values?.leaveComebackDate).toDate(),
				amount: parseFloat(values?.leaveAmount),
				attachmentFile: values?.attachmentFile ? values?.attachmentFile : null,
				attachmentFileSize: values?.attachmentFile?.size ? values?.attachmentFile?.size : null,
			};
			createMutation({ variables })
				.then((result) => {
					toast({
						title: t('Leave created successfully'),
						status: 'success',
						isClosable: true,
						position: 'top-right',
					});
					history.push('/request-leave');
				})
				.catch((error) => {
					toast({
						title: t(error?.message ? error?.message : 'something went wrong'),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					});
				});
		}
	}

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Request Leave',
							path: '/request-leave?page=1',
						},
						{
							name: 'Request',
							path: '#',
						},
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex bg="#FAFAFA" p="16px" pt="8px" pb="8px" mb="0px" justifyContent="space-between" borderBottom="1px solid #dbdbdb">
							<Text fontSize="x-large" fontWeight="bold">
								{t('Request Leave')}
							</Text>
						</Flex>
						<Box p="16px" maxW="480px">
							<Formik
								initialValues={{
									type: '',
									leaveAmount: 1,
									leaveFromDate: new Date(),
									leaveToDate: new Date(),
									leaveComebackDate: new Date(),
									purpose: '',
									attachmentFile: null,
								}}
								validationSchema={validationSchema}
								onSubmit={submit}
							>
								{({ values, errors, ...props }) => {
									return (
										<Box>
											<Form>
												<RadioFormField
													name="type"
													label="Type"
													radios={[
														{
															label: 'Annual leave',
															secondLabel: ` (${
																dataLeaveLeft?.getLeaveAmountLeft?.annualLeave
																	? dataLeaveLeft?.getLeaveAmountLeft?.annualLeave
																	: '0'
															})`,
															value: 'ANNUAL_LEAVE',
														},
														{
															label: 'Short leave',
															secondLabel: ` (${
																dataLeaveLeft?.getLeaveAmountLeft?.shortLeave
																	? dataLeaveLeft?.getLeaveAmountLeft?.shortLeave
																	: '0'
															})`,
															value: 'SHORT_LEAVE',
														},
														// { label: 'Maternity leave', value: 'MATERNITY_LEAVE' },
														// { label: 'Sick leave', value: 'SICK_LEAVE' },
														// { label: 'Personal leave', value: 'PERSONAL_LEAVE' },
														/*
														 * { label: 'Work from home', value: 'WORK_FROM_HOME' }
														 */
													]}
													required
												/>
												<InputNumberFormField
													{...props}
													label="Leave amount (Days)"
													name="leaveAmount"
													min={0.5}
													max={15}
													step={0.5}
													precision={1}
													required
												/>
												<DateTimeFormField
													{...props}
													label={t('leaveFromDate')}
													name="leaveFromDate"
													value={values?.leaveFromDate}
													from={true}
													required
												/>
												<DateTimeFormField
													{...props}
													label={t('leaveToDate')}
													name="leaveToDate"
													value={values?.leaveToDate}
													to={true}
													required
												/>
												<DateTimeFormField
													{...props}
													label={t('leaveComebackDate')}
													name="leaveComebackDate"
													value={values?.leaveComebackDate}
													comeback={true}
													required
												/>
												<TextAreaFormField {...props} label="Purpose" name="purpose" required={values?.type === 'ANNUAL_LEAVE'} />
												<>
													{values?.type && (
														<>
															<FileFormField
																{...props}
																label="Attachment File"
																name="attachmentFile"
																maxFileSize={c.maxFileSize / 20}
															/>
														</>
													)}
												</>
												<Button
													id="login-btn"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													type="submit"
													borderRadius={c.borderRadius}
													borderWidth={errors?.createRestriction ? 2 : 0}
													borderColor="red.400"
													isLoading={loading}
													_focus={{ boxShadow: 'none' }}
												>
													{t('Submit')}
												</Button>
												<Field name="createRestriction">
													{({ meta }) => {
														return (
															<FormControl id="createRestriction" mt={2}>
																{meta.error && (
																	<>
																		<FormHelperText id={`error-message-createRestriction`} color="red.400" fontSize="14px">
																			{t(meta.error)}
																		</FormHelperText>
																		<List spacing={1} mt={2}>
																			<ListItem color="red.400">
																				<ListIcon as={FaExclamationCircle} />
																				{t('Cannot submit leave request for a back date')}
																			</ListItem>
																			<ListItem color="red.400">
																				<ListIcon as={FaExclamationCircle} />
																				{t('Leave request for the current day in the morning has to be before 8:30 AM')}
																			</ListItem>
																			<ListItem color="red.400" fontSize="sm" width="500px">
																				<ListIcon as={FaExclamationCircle} />
																				{t(
																					'Leave request for the current day in the afternoon has to be before 12:00 PM',
																				)}
																			</ListItem>
																		</List>
																	</>
																)}
															</FormControl>
														);
													}}
												</Field>
											</Form>
										</Box>
									);
								}}
							</Formik>
						</Box>
					</Box>
				</Center>
			</Container>
			<AlertDialog motionPreset="slideInBottom" leastDestructiveRef={cancelRef} onClose={onClose} isOpen={isOpen} isCentered>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader>{t('Request Leave')}</AlertDialogHeader>
					<AlertDialogCloseButton />
					<AlertDialogBody>
						{t('Please input')} "{t('Leave from date')}" - "{t('Leave to date')}" {t('in the same year or you can submit 2 requests')}
					</AlertDialogBody>
					<AlertDialogFooter>
						<Button ref={cancelRef} onClick={onClose}>
							{t('Okay')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			{loading && <LoadingOverlayText />}
		</>
	);
}

export default AddLeaveRequest;
